import { Box, Popover, Portal, Skeleton, Text, VStack } from '@chakra-ui/react';
import { parseAsString, useQueryState } from 'nuqs';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FilterButton,
  FilterButtonAction,
  FilterButtonActiveOptions,
  FilterButtonLabel,
  FilterButtonMenu,
  FilterButtonMenuHeader,
  FilterMenuItem,
} from '@/components/filters/filter-button';
import { useListProgram } from '@/hooks/queries/use-list-program';

interface ProgramFilterProps {
  /**
   * Query-string key backing the selection. Defaults to the shared `programIds`
   * param so existing callers keep their URLs; pass a namespaced key (the way
   * `StatusFilter` takes a `paramPrefix`) when a table owns its own filter
   * state and must not collide with another one on the same route.
   */
  paramKey?: string;
}

export const ProgramFilter: React.FC<ProgramFilterProps> = ({ paramKey = 'programIds' }) => {
  const { t } = useTranslation();

  const [urlProgramIds, setUrlProgramIds] = useQueryState(paramKey, parseAsString);

  const selectedProgramIds = useMemo(
    () => (urlProgramIds ? urlProgramIds.split(',').filter(Boolean) : []),
    [urlProgramIds]
  );

  const { data: programs = [], isLoading } = useListProgram();

  const [tempProgramIds, setTempProgramIds] = useState<string[]>(selectedProgramIds);

  useEffect(() => {
    setTempProgramIds(selectedProgramIds);
  }, [selectedProgramIds]);

  const handleApply = useCallback(() => {
    setUrlProgramIds(tempProgramIds.length > 0 ? tempProgramIds.join(',') : null);
  }, [tempProgramIds, setUrlProgramIds]);

  const handleClear = useCallback(() => {
    setTempProgramIds([]);
    setUrlProgramIds(null);
  }, [setUrlProgramIds]);

  const handleClose = useCallback(() => {
    setTempProgramIds(selectedProgramIds);
  }, [selectedProgramIds]);

  const handleProgramSelectionToggle = (programId: string) => {
    setTempProgramIds((prevIds) =>
      prevIds.includes(programId)
        ? prevIds.filter((id) => id !== programId)
        : [...prevIds, programId]
    );
  };

  const isFilterActive = selectedProgramIds.length > 0;

  const renderActiveOptions = () => {
    if (selectedProgramIds.length === 1) {
      const program = programs.find((p) => p.id === selectedProgramIds[0]);
      return (
        <Text as="span" lineClamp={1}>
          {program?.name ?? selectedProgramIds[0]}
        </Text>
      );
    }
    return t('filters.program.selectedCount', { count: selectedProgramIds.length });
  };

  return (
    <FilterButton isFilterActive={isFilterActive} onClearFilter={handleClear} onClose={handleClose}>
      <FilterButtonAction>
        <FilterButtonLabel>{t('filters.program.label')}</FilterButtonLabel>
        {isFilterActive && (
          <FilterButtonActiveOptions>{renderActiveOptions()}</FilterButtonActiveOptions>
        )}
      </FilterButtonAction>
      <Portal>
        <Popover.Positioner>
          <FilterButtonMenu onApply={handleApply} maxWidth="250px">
            <FilterButtonMenuHeader title={t('filters.program.title')} onClearAll={handleClear} />
            <Box p={3}>
              {isLoading ? (
                <Box>
                  <Skeleton height="30px" mb={1} />
                  <Skeleton height="30px" mb={1} />
                  <Skeleton height="30px" />
                </Box>
              ) : (
                <VStack gap={0.5} align="stretch" maxHeight="300px" overflowY="auto">
                  {programs.length === 0 && (
                    <Text fontSize="sm" color="dark" textAlign="center" py={2}>
                      {t('filters.program.noPrograms')}
                    </Text>
                  )}
                  {programs.map((program) => (
                    <FilterMenuItem
                      key={program.id}
                      onClick={() => handleProgramSelectionToggle(program.id)}
                      isSelected={tempProgramIds.includes(program.id)}
                    >
                      <Text fontSize="sm" fontWeight="normal" lineClamp={1}>
                        {program.name}
                      </Text>
                    </FilterMenuItem>
                  ))}
                </VStack>
              )}
            </Box>
          </FilterButtonMenu>
        </Popover.Positioner>
      </Portal>
    </FilterButton>
  );
};
